// src/ProductSearch/ProductSearch.tsx
import * as React from "react";
import Select from "@cloudscape-design/components/select";
import { useAppTranslation } from "@amzn/awsmp-max-translations";
import { jsx } from "react/jsx-runtime";
var LANE_LABEL_KEYS = /* @__PURE__ */ new Map([
  ["SOFTWARE", "productLaneSoftware"],
  ["PROFESSIONAL_SERVICES", "productLaneProfessionalServices"],
  ["MULTI_PRODUCT", "productLaneMultiProduct"],
  ["AI_AGENTS", "productLaneAiAgents"]
]);
var ProductSearch = ({
  maxService,
  onSelect,
  placeholder = "Search for AWS Marketplace products...",
  disabled = false,
  maxResults = 5,
  onError,
  recommendedProducts,
  otherConsideredProducts,
  selectedProductIds = []
}) => {
  const { t } = useAppTranslation();
  const [selectedOption, setSelectedOption] = React.useState(null);
  const [_filteringText, setFilteringText] = React.useState("");
  const [searchResultOptions, setSearchResultOptions] = React.useState([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const searchTimeoutRef = React.useRef();
  const latestRequestIdRef = React.useRef(0);
  const hasGrouping = Boolean(recommendedProducts || otherConsideredProducts);
  const productToOption = React.useCallback(
    (product) => ({
      value: product.productId,
      label: product.productName,
      description: `by ${product.vendorName}`,
      __product: product
    }),
    []
  );
  const options = React.useMemo(() => {
    if (!hasGrouping) {
      return searchResultOptions;
    }
    const groups = [];
    const availableRecommended = (recommendedProducts || []).filter((p) => !selectedProductIds.includes(p.productId));
    const availableOther = (otherConsideredProducts || []).filter((p) => !selectedProductIds.includes(p.productId));
    const allGroupedIds = /* @__PURE__ */ new Set([
      ...(recommendedProducts || []).map((p) => p.productId),
      ...(otherConsideredProducts || []).map((p) => p.productId)
    ]);
    const availableSearchResults = searchResultOptions.filter(
      (opt) => !selectedProductIds.includes(opt.value || "") && !allGroupedIds.has(opt.value || "")
    );
    const hasLanes = [...recommendedProducts || [], ...otherConsideredProducts || []].some((p) => p.pathType);
    const otherLaneOptions = [];
    if (hasLanes) {
      const lanes = /* @__PURE__ */ new Map();
      for (const product of [...availableRecommended, ...availableOther]) {
        const labelKey = LANE_LABEL_KEYS.get(product.pathType || "");
        const lane = labelKey ? lanes.get(labelKey) : otherLaneOptions;
        if (lane)
          lane.push(productToOption(product));
        else if (labelKey)
          lanes.set(labelKey, [productToOption(product)]);
      }
      lanes.forEach((laneOptions, labelKey) => groups.push({ label: t(labelKey), options: laneOptions }));
    }
    if (!hasLanes && availableRecommended.length > 0) {
      groups.push({
        label: "Recommended products",
        options: availableRecommended.map(productToOption)
      });
    }
    if (!hasLanes && availableOther.length > 0) {
      groups.push({
        label: "Other products considered",
        options: availableOther.map(productToOption)
      });
    }
    const otherOptions = [...otherLaneOptions, ...availableSearchResults];
    if (otherOptions.length > 0) {
      groups.push({
        label: "Other",
        options: otherOptions
      });
    }
    return groups;
  }, [hasGrouping, recommendedProducts, otherConsideredProducts, selectedProductIds, searchResultOptions, productToOption, t]);
  const searchProducts = React.useCallback(
    async (query) => {
      if (!query.trim()) {
        setSearchResultOptions([]);
        return;
      }
      const requestId = ++latestRequestIdRef.current;
      setIsSearching(true);
      try {
        const result = await maxService.searchListings({
          searchText: query,
          maxResults,
          includeInsights: false
        });
        if (requestId !== latestRequestIdRef.current) {
          return;
        }
        if (result.success && result.output && typeof result.output === "object" && "output" in result.output) {
          const typedOutput = result.output;
          if (typedOutput.output.searchListings.ListingSummaries) {
            const selectOptions = typedOutput.output.searchListings.ListingSummaries.map((listing) => {
              const logo = listing.Media?.LOGO_THUMBNAIL;
              const logoUrl = logo?.DefaultDomain && logo?.Path ? `${logo.DefaultDomain}${logo.Path}` : void 0;
              const product = {
                productId: listing.Id || "",
                productName: listing.DisplayAttributes?.Title || "Unknown Product",
                vendorName: listing.ProductAttributes?.Creator?.DisplayName || "Unknown Vendor",
                productLogoUrl: logoUrl,
                badges: listing.Badges?.map((b) => ({
                  name: b.DisplayName || "",
                  value: b.Value
                }))
              };
              return {
                value: product.productId,
                label: product.productName,
                description: `by ${product.vendorName}`,
                // Store full product in option for selection
                __product: product
              };
            });
            setSearchResultOptions(selectOptions);
          } else {
            setSearchResultOptions([]);
          }
        } else {
          setSearchResultOptions([]);
        }
      } catch (err) {
        if (requestId !== latestRequestIdRef.current) {
          return;
        }
        console.error("Search failed:", err);
        const errorMessage = err instanceof Error ? err.message : "Failed to search products. Please try again.";
        onError?.(errorMessage);
        setSearchResultOptions([]);
      } finally {
        if (requestId === latestRequestIdRef.current) {
          setIsSearching(false);
        }
      }
    },
    [maxService, maxResults, onError]
  );
  const handleFilteringChange = (filteringText) => {
    setFilteringText(filteringText);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (filteringText.trim()) {
      searchTimeoutRef.current = setTimeout(() => {
        searchProducts(filteringText);
      }, 500);
    } else {
      setSearchResultOptions([]);
    }
  };
  React.useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);
  const handleChange = (option) => {
    if (option && option.__product) {
      const product = option.__product;
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      onSelect(product);
      setSelectedOption(null);
      setFilteringText("");
      setSearchResultOptions([]);
    }
  };
  return /* @__PURE__ */ jsx(
    Select,
    {
      selectedOption,
      onChange: ({ detail }) => handleChange(detail.selectedOption),
      options,
      filteringType: "manual",
      onLoadItems: ({ detail }) => handleFilteringChange(detail.filteringText),
      statusType: isSearching ? "loading" : "finished",
      placeholder,
      empty: "Type to search for products",
      loadingText: "Searching products...",
      disabled,
      virtualScroll: true
    }
  );
};
export {
  ProductSearch
};
//# sourceMappingURL=index.js.map
